/**
 * Display rules for calculated tasks on quotes / invoices.
 */

import {
  getCalculatedLabels,
  isExcavationTemplate,
  cutFillDisplayName,
  excavationMethodName,
  soilTypeName,
  normalizeTemplate
} from './taskTypes'

export function isCalculatedQuoteTask (item) {
  if (!item || item.item_type !== 'task') return false
  const t = item.type
  if (t === 2 || t === '2' || t === 'calculated') return true
  // Saved lines may omit task_type in JSON; calculations imply calculated task.
  if (Array.isArray(item.calculations) && item.calculations.length > 0) return true
  return false
}

export function isTimeBasedQuoteTask (item) {
  if (!item || item.item_type !== 'task') return false
  const t = item.type
  return t === 1 || t === '1' || t === 'time_based' || t === 'time-based'
}

function excavationLine (c, method) {
  const typeName = cutFillDisplayName(c.cut_fill_type || 'cut')
  const L = c.length != null ? Number(c.length) : NaN
  const W = c.width != null ? Number(c.width) : NaN
  const D = c.depth != null ? Number(c.depth) : NaN
  const dims = [L, W, D].every((n) => Number.isFinite(n))
    ? `${L}×${W}×${D} m`
    : ''
  // Method is a task-level setting; a per-row value (legacy) takes precedence when present.
  const resolvedMethod = c.excavation_method || method
  let suffix = ''
  if (c.soil_type || resolvedMethod) {
    const parts = [excavationMethodName(resolvedMethod || 'hand'), soilTypeName(c.soil_type || 'medium')]
      .filter((p) => p)
    suffix = parts.length ? ` — ${parts.join(', ')}` : ''
  } else if (c.location != null && String(c.location).trim() !== '') {
    // Legacy excavation lines stored a numeric soil multiplier.
    suffix = ` — Soil ${c.location}x`
  }
  if (!dims) return `${typeName}${suffix}`
  return `${typeName}: ${dims}${suffix}`
}

export function formatCalculatedLineItemDetails (item) {
  const calcs = item && item.calculations
  if (!calcs || !calcs.length) return ''
  const labels = getCalculatedLabels(item.template || (item.task && item.task.template))
  const excavation = isExcavationTemplate(item.template || (item.task && item.task.template))
  const excavationMethod = item.excavation_method || (item.task && item.task.excavation_method) || 'hand'
  const lines = []
  calcs.forEach((c, i) => {
    if (i > 0) lines.push('')
    if (excavation || c.cut_fill_type || (c.length != null && c.width != null && c.depth != null)) {
      lines.push(excavationLine(c, excavationMethod))
      return
    }
    if (c.material && c.material.description) {
      const m = c.multiplier != null && String(c.multiplier).trim() !== ''
        ? ` — ${c.multiplier} ${labels.quantityUnit || 'm'}`
        : ''
      lines.push(`${labels.primary}: ${c.material.description}${m}`)
    }
    if (labels.hasFittings !== false && Array.isArray(c.fittings)) {
      c.fittings.filter((f) => Number(f.qty) > 0).forEach((f) => {
        lines.push(`${labels.fittings || 'Fitting'}: ${f.description || 'Item'} × ${f.qty}`)
      })
    }
    if (labels.hasFixings !== false && Array.isArray(c.fixings)) {
      c.fixings.filter((f) => Number(f.qty) > 0).forEach((f) => {
        lines.push(`${labels.fixings || 'Fixing'}: ${f.description || 'Item'} × ${f.qty}`)
      })
    }
  })
  return lines.join('\n')
}

function formatTimeBasedVariableDetails (item) {
  const vars = item && item.variables
  if (vars == null) return ''

  let arr = []
  if (Array.isArray(vars)) {
    arr = vars
  } else if (typeof vars === 'object') {
    const d = Array.isArray(vars.default) ? vars.default : []
    const c = Array.isArray(vars.company) ? vars.company : []
    arr = d.concat(c)
  }

  const lines = []
  arr.forEach((v) => {
    if (!v) return
    const desc = v.description || v.name || ''
    if (!desc) return
    const qty = v.qty != null && Number(v.qty) > 0 ? ` × ${v.qty}` : ''
    lines.push(`Product: ${desc}${qty}`)
  })
  return lines.join('\n')
}

/** Text under the line title on quote/invoice when "Show items on quote" is on. */
export function quoteTaskCustomerDescription (item) {
  const base = item && item.description != null ? String(item.description) : ''

  if (!item || item.show_variables !== true) return base

  if (isCalculatedQuoteTask(item)) {
    const extra = formatCalculatedLineItemDetails(item)
    if (!extra) return base
    return base ? `${base}\n\n${extra}` : extra
  }

  // Time-based variable tasks (type 1) also support appending selected variables.
  if (isTimeBasedQuoteTask(item)) {
    const extra = formatTimeBasedVariableDetails(item)
    if (!extra) return base
    return base ? `${base}\n\n${extra}` : extra
  }

  return base
}

export { normalizeTemplate }
